import React from 'react'
import ImgUtil from '../../../utils/img.utils'
import { renderRichText } from '../../../utils/richTextHandler.utils'

import { BioByline, BioHero, BioShortDescription } from './style'

import Grid from '@material-ui/core/Grid'
import BlogRobot from '../../../../static/steam-img/uipath-robot-blog.png'
import { blogUseCaseUrlMappings, UseCaseTypes } from '../../../constants/blogUseCase'
import { generateBlogPostSlug, generateSecondaryBlogSlug } from '../../../utils/blog.utils'
import Breadcrumbs from '../breadcrumbs'
import GlobalContainer from '../globalContainer'
import Typography from '../typography'
import WrapperImage from '../wrapperImage'
type BioProps = {
  data: any
  type?: 'bioShortDescription' | 'bioHero' | 'bioByline'
  className?: string
  location?: any
  pageTitle?: string
  locale?: string
  isSecondaryBlog?: boolean
  useCase?: string
}

const Bio = ({
  data,
  type = 'bioHero',
  className,
  isSecondaryBlog,
  locale,
  useCase = '',
}: BioProps) => {
  if (data) {
    const firstName = data.firstName ? data.firstName : ''
    const lastName = data.lastName ? data.lastName : ''
    const role = data.role ? data.role : ''
    const company = data.company ? data.company : ''
    const miniBio = data.miniBio ? data.miniBio : ''
    const twitterProfile = data.twitterProfile ? data.twitterProfile : ''
    const linkedInProfile = data.linkedInProfile ? data.linkedInProfile : ''

    let headshot = ''
    let headshotAlt = ''

    if (data.headshot) {
      if (data.headshot.altText) headshotAlt = data.headshot.altText
      if (data.headshot.image && data.headshot.image.file && data.headshot.image.file.url)
        headshot = data.headshot.image.file.url
    }

    const cn = className ? { className: className } : {}

    let authorSlug = `${firstName} ${lastName}`
      .toLowerCase()
      .replace(/ *\([^)]*\) */g, '')
      .replace(/\s/g, '-')

    let href = ''
    if (isSecondaryBlog) {
      href = generateSecondaryBlogSlug({
        slug: '',
        blogCategoryName: authorSlug,
        useCase,
        pageLocale: locale,
      })
    } else {
      href = generateBlogPostSlug({
        slug: '',
        blogCategoryName: authorSlug,
        pageLocale: locale,
      })
    }
    switch (type) {
      case 'bioShortDescription':
        return (
          <BioShortDescription {...cn} data-cy="BioShortDescription">
            {headshot && (
              <WrapperImage
                src={headshot}
                alt={headshotAlt}
                width={80}
                height={80}
                threshold={650}
              />
            )}
            <div>
              {firstName && lastName && (
                <a href={href}>
                  {firstName} {lastName}
                </a>
              )}
              <Typography className="BioHero__Short-Description" type="body">
                {role && company ? `${role}, ${company}` : miniBio && renderRichText(miniBio)}
              </Typography>
            </div>
          </BioShortDescription>
        )
      case 'bioByline':
        return (
          <BioByline {...cn} data-cy="BioByline">
            {headshot && (
              <WrapperImage
                src={headshot}
                alt={headshotAlt}
                width={80}
                height={80}
                threshold={650}
              />
            )}
            {firstName && lastName && (
              <>
                <Typography type="body">by</Typography>
                <a href={href}>
                  {firstName} {lastName}
                </a>
              </>
            )}
          </BioByline>
        )
      case 'bioHero':
        return (
          <BioHero {...cn} data-cy="BioHero">
            <GlobalContainer className="BioHero__Outer-Container">
              <Breadcrumbs
                theme="light"
                currentPathname={
                  useCase !== UseCaseTypes.MAIN_BLOG
                    ? `/${blogUseCaseUrlMappings[useCase]}/`
                    : `/blog`
                }
                pageName={`${firstName} ${lastName}`}
                className="BioHero__Breadcrumb"
                isBlogBreadcrumb={true}
                locale={locale}
                isSecondaryBlog={isSecondaryBlog}
              />
              <Grid container className="BioHero__Inner-Container" spacing={3}>
                <Grid item className="BioHero__Text-Container" sm={6} md={6} lg={5}>
                  {(firstName || lastName) && (
                    <Typography type="header1">
                      {firstName} {lastName}
                    </Typography>
                  )}
                  {(role || company) && (
                    <Typography type="header6">
                      {`${role} ${company ? (role ? `- ${company}` : company) : ''}`}
                    </Typography>
                  )}
                  {miniBio && (
                    <Typography className="BioHero__Body" type="body">
                      {renderRichText(miniBio)}
                    </Typography>
                  )}
                  {(linkedInProfile || twitterProfile) && (
                    <div className="BioHero__Social-Container">
                      <Typography type="label">Follow on:</Typography>
                      {linkedInProfile && (
                        <a href={linkedInProfile} target="_blank">
                          <svg
                            viewBox="6 7 19 19"
                            xmlns="https://www.w3.org/2000/svg"
                            fill="#000000"
                            fillRule="evenodd">
                            <path d="M11.791 13.85v9.954H8.477v-9.955h3.314zm.211-3.074A1.59 1.59 0 0 1 11.495 12c-.345.328-.799.492-1.361.492h-.02c-.55 0-.991-.164-1.326-.492a1.646 1.646 0 0 1-.502-1.225c0-.496.172-.906.517-1.23.345-.326.795-.488 1.351-.488.556 0 1.001.162 1.336.487.335.325.506.735.512 1.23zm4.922 4.488c.128-.201.258-.378.392-.53.14-.161.33-.336.567-.523a2.61 2.61 0 0 1 .874-.437 3.982 3.982 0 0 1 1.15-.156c1.145 0 2.066.38 2.763 1.14.696.76 1.044 1.874 1.044 3.34v5.706H20.41V18.48c0-.703-.136-1.254-.407-1.652-.271-.399-.695-.598-1.27-.598-.423 0-.776.115-1.06.346a2.136 2.136 0 0 0-.638.86c-.074.2-.11.471-.11.813v5.555h-3.306c.014-2.672.02-4.839.02-6.5 0-1.66-.003-2.651-.01-2.973l-.01-.482h3.305v1.415z"></path>
                          </svg>
                        </a>
                      )}
                      {twitterProfile && (
                        <a href={twitterProfile} target="_blank">
                          <svg
                            viewBox="6 7 19 19"
                            xmlns="https://www.w3.org/2000/svg"
                            fill="#000000"
                            fillRule="evenodd">
                            <path d="M23.915 11.67a6.798 6.798 0 0 1-1.627 1.677 9.217 9.217 0 0 1-.372 3.028 9.42 9.42 0 0 1-1.16 2.497 9.812 9.812 0 0 1-1.853 2.114c-.717.613-1.58 1.102-2.592 1.467a9.484 9.484 0 0 1-3.244.547 9.01 9.01 0 0 1-4.982-1.456c.234.026.495.04.783.04 1.507 0 2.85-.462 4.028-1.386a3.163 3.163 0 0 1-1.888-.648 3.173 3.173 0 0 1-1.145-1.602c.22.033.425.05.612.05.288 0 .573-.037.854-.11a3.192 3.192 0 0 1-1.863-1.12 3.135 3.135 0 0 1-.738-2.065v-.04c.455.254.944.392 1.466.412A3.24 3.24 0 0 1 9.14 13.92a3.161 3.161 0 0 1-.392-1.547c0-.59.147-1.135.442-1.638a9.197 9.197 0 0 0 2.958 2.396 9.04 9.04 0 0 0 3.731 1 3.604 3.604 0 0 1-.08-.744c0-.897.317-1.662.95-2.295a3.127 3.127 0 0 1 2.295-.95c.937 0 1.727.342 2.37 1.025a6.362 6.362 0 0 0 2.06-.783 3.15 3.15 0 0 1-1.427 1.788 6.464 6.464 0 0 0 1.868-.502z" />
                          </svg>
                        </a>
                      )}
                    </div>
                  )}
                </Grid>
                {headshot && (
                  <div className="BioHero__Image-Container">
                    <WrapperImage
                      className="BioHero__Image-Author"
                      src={ImgUtil.getResizedImageUrl(headshot, '')}
                      alt={headshotAlt}
                      width={265}
                      height={265}
                      threshold={650}
                    />
                    <WrapperImage
                      className="BioHero__Image-Robot"
                      src={BlogRobot}
                      alt="Blue robot"
                      width={144}
                      height={151}
                      threshold={650}
                    />
                  </div>
                )}
              </Grid>
            </GlobalContainer>
          </BioHero>
        )
    }
  } else {
    return null
  }
}

export default Bio
